import { useStore } from "@nanostores/react"
import { useEffect, useMemo } from "react"

import { AO, L1_AR } from "../settings"
import { $tokenInfoCache } from "../stores/token-info-store"
import { getTokenInfo, TokenInfo } from "@/api/token-api"

export const W_AR: TokenInfo = {
  id: "xU9zFkq3X2ZQ6olwNVvr1vUWIjc3kXTWr7xKQD6dh10",
  name: "Wrapped AR",
  ticker: "wAR", // patched
  denomination: 12,
  logo: "L99jaxRKQKJt9CqoJtPaieGPEhJD3wNhR4iGqc8amXs",
}

const Q_AR: TokenInfo = {
  id: "NG-0lVX882MG5nhARrSzyprEK6ejonHpdUmaaMPsHE8",
  denomination: 12,
  ticker: "qAR",
  logo: "26yDr08SuwvNQ4VnhAfV4IjJcOOlQ4tAQLc1ggrCPu0",
  name: "Q Arweave",
}

export const PRE_CACHED_TOKENS: Record<string, TokenInfo> = {
  [Q_AR.id]: Q_AR,
  [W_AR.id]: W_AR,
  [L1_AR]: {
    id: L1_AR,
    denomination: 12,
    ticker: "AR",
    logo: "MfjfWFDOi_3CKO5fW4zUo-B4LibH_IXU8gye941oNq0",
    name: "AR",
  },
  [AO]: {
    id: AO,
    denomination: 12,
    ticker: "AO",
    logo: "UkS-mdoiG8hcAClhKK8ch4ZhEzla0mCPDOix9hpdSFE",
    name: "AO",
  },
  "8rbAftv7RaPxFjFk5FGUVAVCSjGQB4JHDcb9P9wCVhQ": {
    id: "8rbAftv7RaPxFjFk5FGUVAVCSjGQB4JHDcb9P9wCVhQ",
    name: "Agent",
    ticker: "AGENT",
    denomination: 18,
    logo: "j4u-gMaGNQhXFuh8jqDJkrvGh58M2CD_uvK6kah2NFY",
  },
}

export function useTokenInfo(tokenId = "", timeout?: number): [TokenInfo | undefined, boolean] {
  const tokenIds = useMemo(() => [tokenId], [tokenId])
  const [tokens, loading] = useTokenInfos(tokenIds, timeout)
  return [tokens?.[0], loading]
}

export function useTokenInfos(
  tokenIds: string[],
  timeout?: number,
): [TokenInfo[] | undefined, boolean] {
  const cacheMap = useStore($tokenInfoCache, {
    keys: tokenIds,
  })

  const tokenInfos: TokenInfo[] = useMemo(() => {
    return tokenIds
      .map((tokenId) => {
        if (tokenId in PRE_CACHED_TOKENS) {
          return PRE_CACHED_TOKENS[tokenId]
        }
        const cachedValue = cacheMap ? cacheMap[tokenId] : undefined
        const isFetched = cachedValue !== undefined
        const isValid = cachedValue !== "loading" && cachedValue !== "error"
        return isFetched && isValid ? JSON.parse(cachedValue) : undefined
      })
      .filter((info) => info !== undefined) as TokenInfo[]
  }, [cacheMap, tokenIds])

  const isLoading = useMemo(() => {
    return tokenIds.some((tokenId) => {
      const cachedValue = cacheMap ? cacheMap[tokenId] : undefined
      return cachedValue === "loading"
    })
  }, [cacheMap, tokenIds])

  // TODO
  // const isLoading = useMemo(() => {
  //   return tokenIds.length !== tokenInfos.length
  // }, [cacheMap, tokenIds])

  useEffect(() => {
    const cacheMap = $tokenInfoCache.get()
    tokenIds.forEach((tokenId) => {
      const cachedValue = cacheMap ? cacheMap[tokenId] : undefined
      const isFetched = cachedValue !== undefined
      const isLoading = cachedValue === "loading"

      if (isFetched || isLoading || tokenId in PRE_CACHED_TOKENS || !tokenId) return

      $tokenInfoCache.setKey(tokenId, "loading")
      getTokenInfo(tokenId, timeout)
        .then((tokenInfo) => {
          $tokenInfoCache.setKey(tokenId, JSON.stringify(tokenInfo))
        })
        .catch((error) => {
          console.error(error)
          $tokenInfoCache.setKey(tokenId, "error")
        })
    })
  }, [tokenIds, timeout])

  return [tokenInfos, isLoading]
}
